import { Box, Grid, Typography, Stack } from '@mui/material';
import { UserObj } from '@Reducers/AccountLookup/Type';
import React from 'react';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeIcon from '@mui/icons-material/Badge';

interface CardProps {
  title: string;
  value: string;
  icon?: React.ReactNode;
}

function Card(props: CardProps) {
  return (
    <Box
      sx={{
        padding: '20px',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: 'background.paper',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Box
          sx={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'primary.light',
            color: 'primary.main',
          }}
        >
          {props.icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '14px',
            letterSpacing: '0.02em',
          }}
        >
          {props.title}
        </Typography>
      </Stack>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: 1.3,
          color: 'text.primary',
          wordBreak: 'break-word',
          fontFeatureSettings: '"tnum" on, "lnum" on',
        }}
      >
        {props.value || 'N/A'}
      </Typography>
    </Box>
  );
}

interface UserDetailProps {
  user: UserObj | null;
}

function UserDetailCard({ user }: UserDetailProps) {
  if (!user) return null;

  const { _accountId, email, _belongToAdminId, name } = user || {};
  const isIndividualAccount = _accountId?.accountType === 'individual';
  const companyName = isIndividualAccount ? name : _accountId?._company?.[0]?.name;

  const cardData = [
    {
      title: 'Account Number',
      value: _accountId?.accountNumber || 'N/A',
      icon: <BadgeIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: isIndividualAccount ? 'Name' : 'Company Name',
      value: companyName || 'N/A',
      icon: isIndividualAccount ? <PersonIcon sx={{ fontSize: 20 }} /> : <BusinessIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Email',
      value: email || 'N/A',
      icon: <EmailIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Belongs To',
      value: _belongToAdminId?.name || 'N/A',
      icon: <AccountBalanceIcon sx={{ fontSize: 20 }} />,
    },
  ];

  return (
    <Grid container spacing={2.5}>
      {cardData.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card title={card.title} value={card.value} icon={card.icon} />
        </Grid>
      ))}
    </Grid>
  );
}

export default UserDetailCard;
